import { useState } from 'react';
import {
  ActionIcon,
  Box,
  Button,
  Grid,
  NumberInput,
  Skeleton,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';
import { IconPlus, IconTrash } from '@tabler/icons-react';

import type {
  EnvironmentalCalculations,
  EnvironmentalDataFormData,
} from '../../../types/reports.js';
import inputMaterialsCatalog from '../../../data/inputMaterials.json';
import {
  MATRIX_LABEL_TH,
  MATRIX_TABLE_STYLES,
  MATRIX_TYPE_SELECT_TH,
  MATRIX_VALUE_TH,
} from '../../form/matrixPresets.js';
import { DECIMAL_INPUT_PROPS } from '../../form/numberFieldPresets.js';
import {
  ValidatedNumberInput,
  ValidatedSelect,
  ValidatedTextInput,
} from '../../form/validatedFields.js';
import { PreviousValueForField } from '../../form/PreviousValueLine.js';
import { RegisterFieldTab } from '../../form/RegisterFieldTab.js';
import { FormSubsection } from '../../ui/FormSubsection.js';

interface EinsatzstoffeSectionProps {
  form: UseFormReturnType<EnvironmentalDataFormData>;
  materialCalc: EnvironmentalCalculations | null;
  disabled?: boolean;
  loading?: boolean;
}

// Katalogeintrag der 16 Einsatzstoffe. `type` (primary/secondary) ist reine Anzeige an der
// Katalogzeile -- siehe die Text-Zelle unten; kein Formularfeld, kein Feldpfad.
interface InputMaterialCatalogEntry {
  key: string;
  label: string;
  group: string;
  type: 'primary' | 'secondary';
}

// Rohdaten: die JSON-Datei ist die EINZIGE Quelle des Katalogs -- ihr Zwilling im Backend
// (backend/resources/environmental/input-materials.json) haelt ein Gleichstandstest
// (InputMaterialCatalogFileTest, Plan 31-01) synchron. Eine Umsortierung dieser Datei verschiebt
// still die Formularindizes (siehe MATERIAL_INDEX unten und hydrateInputMaterials in
// ReportEditPage.tsx) -- an der Reihenfolge nichts aendern, nur ergaenzen.
const CATALOG_ITEMS = (inputMaterialsCatalog as unknown as { items: InputMaterialCatalogEntry[] })
  .items;

interface InputMaterialGroup {
  group: string;
  materials: InputMaterialCatalogEntry[];
}

// Gruppenstruktur aus der flachen Liste ableiten: Gruppenreihenfolge = Reihenfolge des ersten
// Auftretens in der Datei, Reihenfolge innerhalb einer Gruppe = Dateireihenfolge.
const INPUT_MATERIAL_GROUPS: InputMaterialGroup[] = (() => {
  const groups: InputMaterialGroup[] = [];
  const byName = new Map<string, InputMaterialGroup>();
  for (const item of CATALOG_ITEMS) {
    let group = byName.get(item.group);
    if (!group) {
      group = { group: item.group, materials: [] };
      byName.set(item.group, group);
      groups.push(group);
    }
    group.materials.push(item);
  }
  return groups;
})();

// Single Source of Truth fuer die Katalog-Reihenfolge (16 Material-Keys). Exportiert, damit
// ReportEditPage.tsx die dichte Hydrierung (hydrateInputMaterials) gegen denselben Katalog
// erzeugt und keine zweite, driftende Kopie pflegen muss.
export const MATERIAL_KEYS = CATALOG_ITEMS.map((item) => item.key);

const MATERIAL_INDEX = new Map(MATERIAL_KEYS.map((key, idx) => [key, idx]));

const OTHER_MATERIAL_TYPE_OPTIONS = [
  { value: 'primary', label: 'Primär' },
  { value: 'secondary', label: 'Sekundär' },
];

// Die beiden Summenzeilen: je Rohstofftyp die zwei Kennzahlenschluessel aus materialCalc. Als
// Modul-Konstante statt inline, damit die Zeilen nicht bei jedem Render neu entstehen und die
// Schluesselnamen genau EINMAL dastehen.
const RAW_MATERIAL_SUM_ROWS = [
  {
    type: 'primary',
    typeLabel: 'Primär',
    clinkerKey: 'primaryRawMaterialsClinker',
    cementKey: 'primaryRawMaterialsCementMilling',
  },
  {
    type: 'secondary',
    typeLabel: 'Sekundär',
    clinkerKey: 'secondaryRawMaterialsClinker',
    cementKey: 'secondaryRawMaterialsCementMilling',
  },
] as const satisfies readonly {
  type: string;
  typeLabel: string;
  clinkerKey: keyof EnvironmentalCalculations;
  cementKey: keyof EnvironmentalCalculations;
}[];

// Aufbereitung wie in WasserSection (waterConsumption): null/Leerstring -> Leerstring, sonst
// parseFloat. Kein zweiter Rechenweg im Browser -- die vier Summen kommen ausschliesslich aus
// materialCalc (Server-Antwort), NIE aus einer eigenen Schleife ueber form.getValues(). Ein
// zweiter Rechenweg im Browser wuerde vom Server abdriften, und die Zahl im Browser waere die
// ungeprueften (T-31-06).
// Rechtsbuendig wie die Eingabefelder der Tabellen darueber: die tragen ihre Ausrichtung ueber
// `align="right"` an ValidatedNumberInput, das dieselbe `textAlign`-Regel am `input` setzt
// (validatedFields.tsx, messageInputStyles). Die Summen sind keine Formularfelder -- sie kommen
// aus materialCalc und haben keinen Feldpfad --, deshalb hier das blanke NumberInput mit
// derselben Regel statt der Formularkomponente.
const SUM_INPUT_STYLES = { input: { textAlign: 'right' as const } };

function rawMaterialSumValue(value: string | null | undefined): number | '' {
  return value != null && value !== '' ? parseFloat(value) : '';
}

export function EinsatzstoffeSection({
  form,
  materialCalc,
  disabled,
  loading,
}: EinsatzstoffeSectionProps) {
  const otherInputMaterials = form.getValues().otherInputMaterials ?? [];

  // Versionszaehler, der bei jedem Add/Remove der Sonstige-Liste hochzaehlt und in den
  // Zeilen-React-Key einfliesst -> erzwingt ein Remount ALLER Sonstige-Zeilen. Grund:
  // form.removeListItem/insertListItem (Mantine 9.2.1) bumpen den formKey NICHT (nur die
  // hook-level setValues/initialize/reset tun das), also ist form.key('otherInputMaterials.0')
  // vor und nach der Mutation identisch. Bei uncontrolled Inputs wuerde React die verschobenen
  // Zeilen dann nicht neu mounten -> falsche defaultValues (der letzte Eintrag verschwaende
  // optisch statt des angeklickten). Der Zaehler bumpt NUR bei Button-Klicks, nicht beim Tippen,
  // daher bleibt die Fokus-Stabilitaet (Pitfall 3) unberuehrt.
  const [otherListRev, setOtherListRev] = useState(0);

  function addOtherMaterial(): void {
    form.insertListItem('otherInputMaterials', {
      name: '',
      clinkerProduction: null,
      cementMilling: null,
      type: null,
    });
    setOtherListRev((r) => r + 1);
  }

  function removeOtherMaterial(idx: number): void {
    form.removeListItem('otherInputMaterials', idx);
    setOtherListRev((r) => r + 1);
  }

  if (loading) {
    return (
      <Grid>
        {[0, 1, 2, 3].map((i) => (
          <Grid.Col span={12} key={i}>
            <Skeleton height={100} />
          </Grid.Col>
        ))}
      </Grid>
    );
  }

  return (
    <Stack gap="md">
      <RegisterFieldTab field="inputMaterials" label="Einsatzstoffe" />
      {INPUT_MATERIAL_GROUPS.map((group) => (
        <FormSubsection key={group.group} title={group.group}>
          <Box style={{ overflowX: 'auto' }}>
            <Table striped="odd" styles={MATRIX_TABLE_STYLES}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={MATRIX_LABEL_TH}>Einsatzstoff</Table.Th>
                  {/* Bewusst dasselbe Spaltenmass wie in der Sonstige-Tabelle, obwohl hier nur
                      eine Textzelle steht: die Typ-Spalte soll in beiden Tabellen gleich breit
                      sein, sonst springt der Blick beim Wechsel zwischen ihnen. Die Konstante
                      ist am laengsten Optionstext des Selects bemessen (siehe matrixPresets). */}
                  <Table.Th style={MATRIX_TYPE_SELECT_TH}>Typ</Table.Th>
                  <Table.Th style={MATRIX_VALUE_TH}>Klinkerproduktion (t)</Table.Th>
                  <Table.Th style={MATRIX_VALUE_TH}>Zementmahlung (t)</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {group.materials.map((material) => {
                  const idx = MATERIAL_INDEX.get(material.key);
                  return (
                    <Table.Tr key={material.key}>
                      <Table.Td>
                        <Text size="sm">{material.label}</Text>
                      </Table.Td>
                      <Table.Td>
                        {/* Typ kommt ausschliesslich aus dem Katalog, reine Textzelle -- kein
                            Formularfeld. Laege der Typ in den Meldungsdaten, koennte ein Werk
                            z. B. Kalkstein als Sekundaerrohstoff melden und die
                            Branchensummen waeren unvergleichbar. */}
                        <Text size="sm" c="vdzGray.6">
                          {material.type === 'primary' ? 'Primär' : 'Sekundär'}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <ValidatedNumberInput
                          form={form}
                          field={`inputMaterials.${idx}.clinkerProduction`}
                          kind="decimal"
                          unit="t"
                          align="right"
                          disabled={disabled}
                          {...DECIMAL_INPUT_PROPS}
                        />
                      </Table.Td>
                      <Table.Td>
                        <ValidatedNumberInput
                          form={form}
                          field={`inputMaterials.${idx}.cementMilling`}
                          kind="decimal"
                          unit="t"
                          align="right"
                          disabled={disabled}
                          {...DECIMAL_INPUT_PROPS}
                        />
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Box>
        </FormSubsection>
      ))}

      <FormSubsection
        title="Sonstige Einsatzstoffe"
        description="Zusätzliche Einsatzstoffe, die nicht in den Standardkategorien enthalten sind."
      >
        <RegisterFieldTab field="otherInputMaterials" label="Sonstige Einsatzstoffe" />
        <Stack gap="md">
          {otherInputMaterials.length > 0 && (
            <Box style={{ overflowX: 'auto' }}>
              <Table striped="odd" styles={MATRIX_TABLE_STYLES}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={MATRIX_LABEL_TH}>Bezeichnung</Table.Th>
                    <Table.Th style={MATRIX_TYPE_SELECT_TH}>Typ</Table.Th>
                    <Table.Th style={MATRIX_VALUE_TH}>Klinkerproduktion (t)</Table.Th>
                    <Table.Th style={MATRIX_VALUE_TH}>Zementmahlung (t)</Table.Th>
                    <Table.Th w={50} />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {otherInputMaterials.map((_, idx) => (
                    // Key enthaelt otherListRev: removeListItem/insertListItem bumpen den Mantine-
                    // formKey NICHT, daher wuerde ein index- oder form.key-basierter Schluessel die
                    // verschobenen uncontrolled-Zeilen nach einer Mutation nicht neu mounten (falscher
                    // defaultValue). Der Versionszaehler erzwingt das Remount aller Zeilen bei Add/Remove.
                    <Table.Tr key={`om-${otherListRev}-${idx}`}>
                      <Table.Td>
                        <ValidatedTextInput
                          form={form}
                          field={`otherInputMaterials.${idx}.name`}
                          placeholder="Bezeichnung"
                          disabled={disabled}
                        />
                      </Table.Td>
                      <Table.Td>
                        {/* Keine eigene Validierung, kein eigener onChange-Handler: Fehler
                            kommen aus der Backend-Pipeline (Plan 31-05 markiert genau dieses
                            Feld, sobald eine Menge ohne Typ eingereicht wird). */}
                        <ValidatedSelect
                          form={form}
                          field={`otherInputMaterials.${idx}.type`}
                          data={OTHER_MATERIAL_TYPE_OPTIONS}
                          placeholder="Typ wählen"
                          clearable
                          disabled={disabled}
                        />
                      </Table.Td>
                      <Table.Td>
                        <ValidatedNumberInput
                          form={form}
                          field={`otherInputMaterials.${idx}.clinkerProduction`}
                          kind="decimal"
                          unit="t"
                          align="right"
                          disabled={disabled}
                          {...DECIMAL_INPUT_PROPS}
                        />
                      </Table.Td>
                      <Table.Td>
                        <ValidatedNumberInput
                          form={form}
                          field={`otherInputMaterials.${idx}.cementMilling`}
                          kind="decimal"
                          unit="t"
                          align="right"
                          disabled={disabled}
                          {...DECIMAL_INPUT_PROPS}
                        />
                      </Table.Td>
                      <Table.Td>
                        <ActionIcon
                          color="red"
                          variant="subtle"
                          onClick={() => removeOtherMaterial(idx)}
                          disabled={disabled}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Box>
          )}

          <Button
            leftSection={<IconPlus size={16} />}
            variant="light"
            onClick={addOtherMaterial}
            disabled={disabled}
          >
            Einsatzstoff hinzufügen
          </Button>
        </Stack>
      </FormSubsection>

      <FormSubsection
        title="Rohstoffsummen"
        description="Automatisch berechnet: Summe über Katalogstoffe und sonstige Einsatzstoffe gemeinsam, aufgeteilt nach Primär- und Sekundärrohstoffen."
      >
        {/* Dieselbe Tabellenform wie die Katalog- und die Sonstige-Tabelle darueber (gleiche
            Spaltenbreiten aus matrixPresets, gleiche Kopfzeilen) -- die Summen lesen sich damit
            als Abschluss derselben Aufstellung statt als eigener Block aus vier Feldern.
            Spalte 1 traegt "Summe", Spalte 2 den Rohstofftyp; beides steht oben an derselben
            Stelle (Stoffname, Typ).

            Werte kommen ausschliesslich aus materialCalc (Server-Antwort), NIE aus einer eigenen
            Summierung ueber form.getValues() -- siehe rawMaterialSumValue oben. Dargestellt wie
            zuvor als schreibgeschuetztes NumberInput: dieselbe Zellenoptik wie die Eingabespalten
            darueber, nur gesperrt. */}
        <Box style={{ overflowX: 'auto' }}>
          <Table striped="odd" styles={MATRIX_TABLE_STYLES}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={MATRIX_LABEL_TH}>Einsatzstoff</Table.Th>
                <Table.Th style={MATRIX_TYPE_SELECT_TH}>Typ</Table.Th>
                <Table.Th style={MATRIX_VALUE_TH}>Klinkerproduktion (t)</Table.Th>
                <Table.Th style={MATRIX_VALUE_TH}>Zementmahlung (t)</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {RAW_MATERIAL_SUM_ROWS.map((row) => (
                <Table.Tr key={row.type}>
                  <Table.Td>
                    <Text size="sm" fw={600}>Summe</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{row.typeLabel}</Text>
                  </Table.Td>
                  <Table.Td>
                    <NumberInput
                      value={rawMaterialSumValue(materialCalc?.[row.clinkerKey])}
                      readOnly
                      disabled
                      suffix=" t"
                      styles={SUM_INPUT_STYLES}
                      {...DECIMAL_INPUT_PROPS}
                    />
                    <RegisterFieldTab
                      field={row.clinkerKey}
                      label={`Rohstoffsummen ${row.typeLabel}: Klinkerproduktion`}
                    />
                    <PreviousValueForField field={row.clinkerKey} />
                  </Table.Td>
                  <Table.Td>
                    <NumberInput
                      value={rawMaterialSumValue(materialCalc?.[row.cementKey])}
                      readOnly
                      disabled
                      suffix=" t"
                      styles={SUM_INPUT_STYLES}
                      {...DECIMAL_INPUT_PROPS}
                    />
                    <RegisterFieldTab
                      field={row.cementKey}
                      label={`Rohstoffsummen ${row.typeLabel}: Zementmahlung`}
                    />
                    <PreviousValueForField field={row.cementKey} />
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Box>
      </FormSubsection>
    </Stack>
  );
}
